import { Box, Typography } from "@mui/material";
import React, { useState, useMemo } from "react";
import DocumentSlotCard from "./DocumentSlotCard";
import DocumentsStatistics from "./DocumentsStatistics";
import DocumentUploadModal from "./modals/DocumentUploadModal";
import DocumentVersionHistoryModal from "./modals/DocumentVersionHistoryModal";
import SendDocumentWhatsAppModal from "./modals/SendDocumentWhatsAppModal";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal";

/**
 * Componente para exibir visualização de slots de documentos
 * @param {object} props
 * @param {number} props.projectId - ID do projeto
 * @param {object} props.project - Dados completos do projeto
 * @param {array} props.slots - Array de slots de documentos (ProjectDocumentSlotDTO[])
 * @param {object} props.statistics - Estatísticas dos documentos
 * @param {function} props.onRefresh - Callback para atualizar dados
 */
export default function DocumentSlotsView({ projectId, project, slots, statistics, onRefresh }) {
  // Estados dos modais
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Estados para dados selecionados
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Handlers dos modais
  const handleUploadClick = (documentType, file = null) => {
    setSelectedDocumentType(documentType);
    setSelectedFile(file);
    setUploadModalOpen(true);
  };

  const handleHistoryClick = (documentType) => {
    setSelectedDocumentType(documentType);
    setHistoryModalOpen(true);
  };

  const handleWhatsAppClick = (document) => {
    setSelectedDocument(document);
    setWhatsappModalOpen(true);
  };

  const handleDeleteClick = (document) => {
    setSelectedDocument(document);
    setDeleteModalOpen(true);
  };

  const handleUploadSuccess = () => {
    setUploadModalOpen(false);
    setSelectedDocumentType(null);
    setSelectedFile(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleDeleteSuccess = () => {
    setDeleteModalOpen(false);
    setSelectedDocument(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleSendSuccess = () => {
    setWhatsappModalOpen(false);
    setSelectedDocument(null);
  };

  // Ordenar slots por displayOrder
  const sortedSlots = [...(slots || [])].sort(
    (a, b) => a.documentType.displayOrder - b.documentType.displayOrder
  );

  // Agrupar por categoria com ordem fixa
  const categoryOrder = ["Cliente", "Legal", "Técnico", "Administrativo"];

  const groupedSlots = useMemo(() => {
    const groups = {};
    sortedSlots.forEach((slot) => {
      const cat = slot.documentType.category || "Outros";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(slot);
    });
    // Ordenar categorias
    const ordered = {};
    categoryOrder.forEach((cat) => {
      if (groups[cat]) ordered[cat] = groups[cat];
    });
    // Adicionar categorias extras não previstas
    Object.keys(groups).forEach((cat) => {
      if (!ordered[cat]) ordered[cat] = groups[cat];
    });
    return ordered;
  }, [sortedSlots]);

  return (
    <Box>
      {/* Estatísticas */}
      {statistics && <DocumentsStatistics statistics={statistics} />}

      {/* Grupos por categoria */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {Object.entries(groupedSlots).map(([category, categorySlots]) => (
          <Box key={category}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "text.secondary",
                mb: 0.75,
                pl: 0.5,
              }}
            >
              {category}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {categorySlots.map((slot) => (
                <DocumentSlotCard
                  key={slot.documentType.id}
                  slot={slot}
                  projectId={projectId}
                  onUploadClick={handleUploadClick}
                  onHistoryClick={handleHistoryClick}
                  onWhatsAppClick={handleWhatsAppClick}
                  onDeleteClick={handleDeleteClick}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Modais */}
      {uploadModalOpen && (
        <DocumentUploadModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          documentType={selectedDocumentType}
          projectId={projectId}
          initialFile={selectedFile}
          onSuccess={handleUploadSuccess}
        />
      )}

      {historyModalOpen && (
        <DocumentVersionHistoryModal
          isOpen={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          projectId={projectId}
          documentType={selectedDocumentType}
        />
      )}

      {whatsappModalOpen && (
        <SendDocumentWhatsAppModal
          isOpen={whatsappModalOpen}
          onClose={() => setWhatsappModalOpen(false)}
          document={selectedDocument}
          project={project}
          onSuccess={handleSendSuccess}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          document={selectedDocument}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </Box>
  );
}

